import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Paper } from "@mui/material";
import { FiDollarSign, FiTrendingUp, FiUsers, FiTarget } from "react-icons/fi";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area
} from "recharts";

import axios from "axios";
import { FaRupeeSign } from "react-icons/fa";

// --- MOCK DATA BASED ON YOUR DOMAIN ---


// 1. CRM Billings vs Payments
const billingData = [
  { month: "Jan", billed: 45000, received: 32000 },
  { month: "Feb", billed: 52000, received: 38000 },
  { month: "Mar", billed: 48000, received: 40000 },
  { month: "Apr", billed: 61000, received: 45000 },
  { month: "May", billed: 55000, received: 50000 },
  { month: "Jun", billed: 68000, received: 62000 },
];



// 2. Lead Stages Distribution (Site Visit, Call, Blocked, Booked)
const leadStagesData = [
  { name: "Site Visit", value: 45 },
  { name: "Call / Follow-up", value: 30 },
  { name: "Blocked", value: 10 },
  { name: "Booked", value: 15 },
];
const COLORS = ["#10B981", "#4318ff", "#F59E0B", "#EF4444"]; // Green, Purple, Yellow, Red

// 3. Project Sales / Performance Trend (Units Sold over time)
const performanceData = [
  { month: "Jan", sales: 65 },
  { month: "Feb", sales: 72 },
  { month: "Mar", sales: 68 },
  { month: "Apr", sales: 81 },
  { month: "May", sales: 76 },
  { month: "Jun", sales: 89 },
];



export default function AnalyticsDashboard() {
  // --- SUB-COMPONENT: Top Metric Cards ---

    // State to hold our dynamic data
  const [metrics, setMetrics] = useState({
    totalRevenue: 0,
    totalUnitsSold: 0,
    totalLeads: 0,
    conversionRate: 0
  });

  
const companyId = sessionStorage.getItem("companyId");



  // Fetch data on component load
  useEffect(() => {
    if (companyId) {
      axios.get(`https://buildfastvapi.emedha.in/analytics/api/v1/top-metrics?company_id=${companyId}`)
        .then((res) => {
          if (res.data.status === 200) {
            setMetrics(res.data.data);
          }
        })
        .catch((err) => console.log("Error fetching metrics:", err));
    }
  }, [companyId]);

  // Helper function: Formats raw numbers into Crores (Cr) or Lakhs (L)
  const formatIndianCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(2)} K`;
    return `₹${value || 0}`;
  };




  const MetricCard = ({ title, value, percentage, isPositive, icon, iconBg, iconColor }) => (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: "16px",
        border: "1px solid #E0E5F2",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.02)",
        transition: "transform 0.2s ease-in-out",
        "&:hover": { transform: "translateY(-4px)", boxShadow: "0px 10px 25px rgba(0, 0, 0, 0.05)" }
      }}
    >
      <Box>
        <Typography sx={{ color: "#A3AED0", fontSize: "13px", fontWeight: "600", mb: 1 }}>
          {title}
        </Typography>
        <Typography sx={{ color: "#1B2559", fontSize: "24px", fontWeight: "800", mb: 1 }}>
          {value}
        </Typography>
        {/* <Typography sx={{ color: isPositive ? "#059669" : "#E31B21", fontSize: "12px", fontWeight: "700", display: "flex", alignItems: "center", gap: "4px" }}>
          {isPositive ? "↑" : "↓"} {percentage}% <span style={{ color: "#A3AED0", fontWeight: "500" }}>vs last month</span>
        </Typography> */}
      </Box>
      <Box
        sx={{
          width: "56px", height: "56px", borderRadius: "50%",
          display: "flex", justifyContent: "center", alignItems: "center",
          backgroundColor: iconBg, color: iconColor
        }}
      >
        {icon}
      </Box>
    </Paper>
  );


  const [leadStagesData, setLeadStagesData] = useState([]);
  // (Assuming you already have billingData, projectName, and filteredProjects states here)

  // --- 2. Fetch Lead Stages Data ---
  useEffect(() => {
    if (companyId) {
      axios.get(`https://buildfastvapi.emedha.in/analytics/api/v1/chart-lead-stages?company_id=${companyId}`)
        .then((res) => {
          if (res.data.status === 200) {
            setLeadStagesData(res.data.data);
          }
        })
        .catch((err) => console.log(err));
    }
  }, [companyId]);

  const [chartData, setChartData] = useState([]);


  useEffect(() => {
    if (companyId) {
      // Replace YOUR_API_URL with your actual backend URL
      axios.get(`https://buildfastvapi.emedha.in/analytics/api/v1/chart-billing-payments?company_id=${companyId}`)
        .then((res) => {
          if (res.data.status === 200) {
            setChartData(res.data.data);
          }
        })
        .catch((err) => console.log("Error fetching chart data:", err));
    }
  }, [companyId]);

  // Helper function to cleanly format the Y-Axis tooltips and ticks (e.g., 4000000 -> 40L)
  const formatYAxis = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(0)}L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(0)}K`;
    return `₹${value}`;
  };

  const [performanceData, setPerformanceData] = useState([]);

  useEffect(() => {
    if (companyId) {
      // Fetch dynamic sales data
      axios.get(`https://buildfastvapi.emedha.in/analytics/api/v1/chart-sales-performance?company_id=${companyId}`)
        .then((res) => {
          if (res.data.status === 200) {
            setPerformanceData(res.data.data);
          }
        })
        .catch((err) => console.log("Error fetching performance data:", err));
    }
  }, [companyId]);





  return (
    <Box sx={{ p: { xs: 2, md: 4 }, backgroundColor: "#F8F9FF", minHeight: "100vh" }}>
      
      {/* HEADER */}
      <Box sx={{ mb: 4 }}>
        <Typography sx={{ color: "#1B2559", fontSize: "28px", fontWeight: "800", letterSpacing: "-0.5px" }}>
          Analytics
        </Typography>
        <Typography sx={{ color: "#A3AED0", fontSize: "14px", fontWeight: "500" }}>
          Comprehensive business analytics for Projects, Leads, and Billings.
        </Typography>
      </Box>

      {/* 1. TOP CARDS ROW */}
<Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard 
            title="Total Revenue" 
            value={formatIndianCurrency(metrics.totalRevenue)} 
            percentage="--" // You can calculate month-over-month growth later
            isPositive={true} 
            icon={<FaRupeeSign size={21} />} 
            iconBg="#F0FDF4" iconColor="#10B981" 
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard 
            title="Total Units Sold" 
            value={metrics.totalUnitsSold} 
            percentage="--" 
            isPositive={true} 
            icon={<FiTrendingUp size={21} />} 
            iconBg="#EEF2FF" iconColor="#4318ff" 
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {/* Changed Title to Total Leads as requested */}
          <MetricCard 
            title="Total Leads" 
            value={metrics.totalLeads} 
            percentage="--" 
            isPositive={true} 
            icon={<FiUsers size={21} />} 
            iconBg="#FDF4FF" iconColor="#D946EF" 
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard 
            title="Lead Conversion" 
            value={`${metrics.conversionRate}%`} 
            percentage="--" 
            isPositive={true} 
            icon={<FiTarget size={21} />} 
            iconBg="#FFFBEB" iconColor="#F59E0B" 
          />
        </Grid>
      </Grid>
      {/* 2. MIDDLE CHARTS ROW */}
      <Grid container spacing={3} sx={{ mb: 4 }}>

        <Grid item xs={12} md={8}>
     <Paper elevation={0} sx={{ p: 3, borderRadius: "16px", border: "1px solid #E0E5F2", height: "400px" }}>
      <Typography sx={{ color: "#1B2559", fontSize: "16px", fontWeight: "700", mb: 3 }}>
        Billings vs Payments Received
      </Typography>
      
      <ResponsiveContainer width="100%" height="90%">
        <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E0E5F2" />
          
          <XAxis 
            dataKey="month" 
            axisLine={false} 
            tickLine={false} 
            tick={{ fill: "#A3AED0", fontSize: 12, fontWeight: 600 }} 
            dy={10} 
          />
          
          <YAxis 
            axisLine={false} 
            tickLine={false} 
            tick={{ fill: "#A3AED0", fontSize: 12, fontWeight: 600 }} 
            tickFormatter={formatYAxis}
            width={60} // Gives the Y-axis labels enough room to breathe
          />
          
          <RechartsTooltip 
            cursor={{ fill: "#F4F7FE" }} 
            contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0px 4px 15px rgba(0,0,0,0.1)", fontWeight: 600 }}
            formatter={(value) => formatYAxis(value)} // Formats the numbers inside the hover box
          />
          
          <Legend 
            iconType="circle" 
            wrapperStyle={{ paddingTop: "20px", fontSize: "13px", color: "#2B3674", fontWeight: 600 }} 
          />
          
          <Bar dataKey="billed" name="Billed Amount" fill="#4318ff" radius={[4, 4, 0, 0]} barSize={25} />
          <Bar dataKey="received" name="Payments Received" fill="#10B981" radius={[4, 4, 0, 0]} barSize={25} />
        </BarChart>
      </ResponsiveContainer>
    </Paper>
        </Grid>

        {/* Pie Chart: Lead Stages */}
      <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: "16px", border: "1px solid #E0E5F2", height: "400px", display: "flex", flexDirection: "column" }}>
            <Typography sx={{ color: "#1B2559", fontSize: "16px", fontWeight: "700", mb: 1 }}>
              Lead Stage Distribution
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                {/* Inject leadStagesData here */}
                <Pie data={leadStagesData} cx="50%" cy="50%" innerRadius={70} outerRadius={100} paddingAngle={2} dataKey="value" stroke="none">
                  {leadStagesData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip />
                <Legend iconType="circle" layout="horizontal" verticalAlign="bottom" wrapperStyle={{ fontSize: "12px", color: "#2B3674" }} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

      
      </Grid>

      {/* 3. BOTTOM ROW: Performance Area Chart */}
   <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper elevation={0} sx={{ p: 3, borderRadius: "16px", border: "1px solid #E0E5F2", height: "350px", boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.02)" }}>
          <Typography sx={{ color: "#1B2559", fontSize: "16px", fontWeight: "700", mb: 3 }}>
            Sales Performance Trend (Units Booked)
          </Typography>
          
          <ResponsiveContainer width="100%" height="85%">
            <AreaChart data={performanceData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              
              {/* Premium Blue Gradient Definition */}
              <defs>
                <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4318ff" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#4318ff" stopOpacity={0} />
                </linearGradient>
              </defs>
              
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E0E5F2" />
              
              <XAxis 
                dataKey="month" 
                axisLine={false} 
                tickLine={false} 
                tick={{ fill: "#A3AED0", fontSize: 12, fontWeight: 600 }} 
                dy={10} 
              />
              
              <YAxis 
                axisLine={false} 
                tickLine={false} 
                tick={{ fill: "#A3AED0", fontSize: 12, fontWeight: 600 }} 
                allowDecimals={false} // Since you can't sell half a unit
              />
              
              <RechartsTooltip 
                cursor={{ stroke: "#4318ff", strokeWidth: 1, strokeDasharray: "3 3" }} // Cool dashed line on hover
                contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0px 4px 15px rgba(0,0,0,0.1)", fontWeight: 600 }} 
              />
              
              <Area 
                type="monotone" // Smooth curvy line
                dataKey="sales" 
                name="Units Booked" 
                stroke="#4318ff" 
                strokeWidth={3} 
                fillOpacity={1} 
                fill="url(#colorSales)" // Applies the gradient we defined above
              />
              
            </AreaChart>
          </ResponsiveContainer>
        </Paper>
      </Grid>
    </Grid>

    </Box>
  );
}